import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Lightbulb, CheckCircle, Globe, TrendUp, TrendDown } from "@phosphor-icons/react";
import { api } from "../context/AuthContext";
import { VikiAvatar } from "../components/VikiAvatar";

const Stat = ({ label, value, tone = "text-white" }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
    <p className={`font-heading text-2xl font-bold ${tone}`}>{value}</p>
    <p className="text-[11px] text-white/40 mt-0.5">{label}</p>
  </div>
);

export default function MarketingIniziativePage() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/viki/marketing-initiatives").then((r) => setData(r.data)).catch(() => setData({ sites: [] }));
  }, []);

  const toggleDone = async (siteIdx) => {
    const s = data.sites[siteIdx];
    if (!s.oggi) return;
    const next = !s.oggi.done;
    const patch = (val) => setData((d) => ({
      ...d,
      fatte: d.fatte + (val ? 1 : -1),
      in_corso: d.in_corso + (val ? -1 : 1),
      sites: d.sites.map((x, j) => (j === siteIdx
        ? { ...x, fatte: x.fatte + (val ? 1 : -1), oggi: { ...x.oggi, done: val } }
        : x)),
    }));
    patch(next);
    try {
      await api.post("/viki/marketing-tips/done", { day: s.oggi.day, index: s.oggi.index, done: next });
    } catch {
      patch(!next);
    }
  };

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="marketing-iniziative-page">
      <Link to="/marketing" className="inline-flex items-center gap-1.5 text-xs text-white/40 hover:text-white mb-6" data-testid="iniziative-back-link">
        <ArrowLeft size={13} /> Marketing
      </Link>
      <div className="flex items-end justify-between gap-4 mb-8 animate-fade-up">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-2">Marketing digitale</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-none">Iniziative</h1>
          <p className="text-sm text-slate-400 mt-3 max-w-2xl">
            Proposte da V.I.K.I. sui numeri reali dei tuoi siti — massima resa, minima spesa. Segnale come fatte: entrano nel report del venerdì.
          </p>
        </div>
        <VikiAvatar size={52} />
      </div>

      {!data ? (
        <p className="text-sm text-white/40">Caricamento…</p>
      ) : (
        <>
          <div className="rounded-2xl border border-cyan-400/25 bg-gradient-to-br from-cyan-400/[0.06] to-violet-400/[0.05] p-5 mb-10 animate-fade-up" data-testid="iniziative-sintesi">
            <div className="flex items-center gap-2 mb-4">
              <Lightbulb size={18} className="text-cyan-300" />
              <h2 className="font-heading text-lg font-semibold tracking-tight">Sintesi degli ultimi {data.periodo_giorni || 30} giorni</h2>
            </div>
            <div className="grid grid-cols-3 gap-3 max-w-xl">
              <Stat label="Iniziative proposte" value={data.proposte ?? 0} />
              <Stat label="Completate" value={data.fatte ?? 0} tone="text-emerald-300" />
              <Stat label="In corso / da fare" value={data.in_corso ?? 0} tone="text-amber-300" />
            </div>
          </div>

          <h2 className="font-heading text-xl font-semibold tracking-tight mb-1">Le iniziative di oggi, sito per sito</h2>
          <p className="text-xs text-white/40 mb-5">Ogni card è un sito: l'iniziativa di oggi, i suoi numeri e lo storico dedicato.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(data.sites || []).map((s, idx) => {
              const delta = s.clic_prec_7gg > 0 ? Math.round(((s.clic_7gg - s.clic_prec_7gg) / s.clic_prec_7gg) * 100) : null;
              const t = s.oggi;
              return (
                <div key={s.site} className={`rounded-2xl border p-5 transition-colors ${t?.done ? "border-emerald-400/30 bg-emerald-400/[0.04]" : "border-white/10 bg-white/[0.03]"}`}
                  data-testid={`iniziative-site-card-${s.site}`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <Globe size={17} className="text-cyan-300" />
                      <span className="font-heading text-lg font-semibold tracking-tight">{s.site}</span>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border shrink-0 ${(t?.costo || "zero") === "zero" ? "text-emerald-300 border-emerald-400/40" : "text-amber-300 border-amber-400/40"}`}>
                      costo {t?.costo || "zero"}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-white/45 mb-3">
                    <span><b className="text-white/80">{s.proposte}</b> proposte · <b className="text-emerald-300">{s.fatte}</b> fatte</span>
                    <span className="inline-flex items-center gap-1">
                      <b className="text-white/80">{s.clic_7gg}</b> clic · <b className="text-white/80">{s.impression_7gg}</b> impression (7gg)
                      {delta !== null && (
                        <span className={`inline-flex items-center gap-0.5 font-semibold ${delta >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                          {delta >= 0 ? <TrendUp size={11} /> : <TrendDown size={11} />}{delta >= 0 ? "+" : ""}{delta}%
                        </span>
                      )}
                    </span>
                  </div>
                  {t ? (
                    <>
                      <p className={`text-sm font-semibold mb-1 ${t.done ? "line-through text-white/45" : ""}`}>{t.titolo}</p>
                      <p className="text-xs text-white/60 leading-relaxed">{t.azione}</p>
                      {t.perche && <p className="text-[11px] text-white/35 mt-1.5 italic">{t.perche}</p>}
                      {t.misura && <p className="text-[11px] text-cyan-200/50 mt-1">Da misurare: {t.misura}</p>}
                    </>
                  ) : (
                    <p className="text-xs text-white/40">Nessuna iniziativa oggi per questo sito: la prossima arriva domattina.</p>
                  )}
                  <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-white/5">
                    {t ? (
                      <button data-testid={`viki-tip-done-${s.site}`} onClick={() => toggleDone(idx)}
                        className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${t.done
                          ? "text-emerald-300 border-emerald-400/50 bg-emerald-400/10"
                          : "text-white/50 border-white/15 hover:text-emerald-300 hover:border-emerald-400/40"}`}>
                        <CheckCircle size={13} weight={t.done ? "fill" : "regular"} /> {t.done ? "Fatta" : "Segna come fatta"}
                      </button>
                    ) : <span />}
                    <Link to={`/marketing/iniziative/${s.site}`} data-testid={`iniziative-site-link-${s.site}`}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-cyan-300/80 hover:text-cyan-200">
                      Tutte le iniziative <ArrowRight size={12} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </main>
  );
}
